<template>
  <div class="settings-org-section">
    <SettingsSectionHeader
      title="Departments"
      subtitle="Department structure and appearance"
      icon="pi-building"
    >
      <template #actions>
        <Button
          v-if="canCreate"
          label="Add"
          icon="pi pi-plus"
          size="small"
          :disabled="mutating"
          @click="openCreate"
        />
        <Button
          icon="pi pi-refresh"
          label="Refresh"
          size="small"
          severity="secondary"
          outlined
          :loading="loading"
          @click="loadItems"
        />
      </template>
    </SettingsSectionHeader>

    <div class="settings-org-section__body">
      <div v-if="loading && !items.length" class="settings-org-section__state">
        <ProgressSpinner style="width: 2rem; height: 2rem" strokeWidth="4" />
      </div>

      <Message v-else-if="error" severity="error" :closable="false" class="settings-org-section__message">
        <div class="flex flex-col sm:flex-row sm:items-center gap-3">
          <span class="flex-1">{{ error }}</span>
          <Button label="Retry" size="small" severity="danger" outlined @click="loadItems" />
        </div>
      </Message>

      <template v-else>
        <SettingsOrgReferenceToolbar
          v-model:show-inactive="showInactive"
          v-model:search-query="searchQuery"
          :count-label="countLabel"
          inactive-input-id="departments-show-inactive"
          search-placeholder="Search departments..."
          :loading="loading"
        />

        <div v-if="!filteredItems.length" class="settings-org-section__empty">
          <i class="pi pi-building settings-org-section__empty-icon" aria-hidden="true" />
          <p>{{ emptyMessage }}</p>
          <Button
            v-if="searchQuery"
            label="Clear search"
            size="small"
            severity="secondary"
            text
            @click="searchQuery = ''"
          />
        </div>

        <DataTable
          v-else
          :value="filteredItems"
          size="small"
          dataKey="id"
          sort-mode="single"
          removable-sort
          :rowClass="rowClass"
        >
          <Column field="name" header="Name" sortable style="min-width: 10rem">
            <template #body="{ data }">
              <Tag
                :value="data.name"
                :style="getDepartmentTagStyle(data.id, data.name, data.tag_color, data.tag_text_color)"
              />
            </template>
          </Column>
          <Column header="Reports To" style="width: 7rem">
            <template #body="{ data }">
              <Tag
                v-if="data.reporting_branch"
                :value="data.reporting_branch"
                severity="secondary"
              />
              <span v-else class="text-sm text-surface-600 dark:text-surface-400">—</span>
            </template>
          </Column>
          <Column v-if="showInactive" header="Status" style="width: 6.5rem">
            <template #body="{ data }">
              <SettingsOrgReferenceStatusTag :item="data" />
            </template>
          </Column>
          <Column field="employees_count" header="Employees" sortable style="width: 6rem">
            <template #body="{ data }">
              <span class="text-sm text-surface-600 dark:text-surface-400">
                {{ data.employees_count ?? 0 }}
              </span>
            </template>
          </Column>
          <Column field="positions_count" header="Positions" sortable style="width: 6rem">
            <template #body="{ data }">
              <span class="text-sm text-surface-600 dark:text-surface-400">
                {{ data.positions_count ?? 0 }}
              </span>
            </template>
          </Column>
          <Column header="Actions" style="width: 11rem">
            <template #body="{ data }">
              <div class="crc-table-row-actions">
                <Button
                  v-if="canUpdate"
                  icon="pi pi-pencil"
                  size="small"
                  severity="secondary"
                  outlined
                  rounded
                  class="crc-table-action-btn"
                  v-tooltip.top="'Edit'"
                  :disabled="mutating"
                  @click="openEdit(data)"
                />
                <Button
                  v-if="canStatusChange && isOrgReferenceActive(data)"
                  icon="pi pi-ban"
                  size="small"
                  severity="warn"
                  outlined
                  rounded
                  class="crc-table-action-btn"
                  v-tooltip.top="'Deactivate'"
                  :disabled="mutating"
                  @click="confirmDeactivate(data)"
                />
                <Button
                  v-if="canDelete"
                  icon="pi pi-trash"
                  size="small"
                  severity="danger"
                  outlined
                  rounded
                  class="crc-table-action-btn"
                  v-tooltip.top="'Delete'"
                  :disabled="mutating"
                  @click="confirmDelete(data)"
                />
              </div>
            </template>
          </Column>
        </DataTable>
      </template>
    </div>

    <DepartmentEditModal
      v-model:visible="editModalVisible"
      :department="selectedItem"
      :saving="mutating"
      :can-edit="canUpdate"
      :can-delete="canDelete"
      @save="handleSave"
      @delete="handleDelete"
    />
  </div>
</template>

<script setup>
import { computed, onMounted, ref, watch } from 'vue';
import { useToast } from 'primevue/usetoast';
import { useConfirm } from 'primevue/useconfirm';
import Button from 'primevue/button';
import Tag from 'primevue/tag';
import DataTable from 'primevue/datatable';
import Column from 'primevue/column';
import Message from 'primevue/message';
import ProgressSpinner from 'primevue/progressspinner';
import SettingsSectionHeader from '../SettingsSectionHeader.vue';
import SettingsOrgReferenceToolbar from '../SettingsOrgReferenceToolbar.vue';
import SettingsOrgReferenceStatusTag from '../SettingsOrgReferenceStatusTag.vue';
import DepartmentEditModal from '@/components/modals/DepartmentEditModal.vue';
import { useSettingsEntityList } from '../../composables/useSettingsEntityList';
import {
  buildOrgReferenceCountLabel,
  isOrgReferenceActive,
  orgReferenceRowClass,
  sortOrgReferenceRows,
} from '../../utils/settingsOrgReferenceUtils';
import {
  createDepartment,
  deleteDepartment,
  fetchDepartmentsList,
  updateDepartment,
  updateDepartmentStatus,
} from '@/api/departmentsApi';
import { getDepartmentTagStyle } from '@/utils/tagColors';
import { useEntityPermissions } from '@/composables/useAuth';

defineOptions({ name: 'SettingsDepartmentsSection' });

defineProps({
  section: { type: Object, default: null },
});

defineEmits(['close-settings']);

const toast = useToast();
const confirm = useConfirm();
const { canCreate, canUpdate, canDelete, canStatusChange } = useEntityPermissions('departments');
const { showInactive, afterMutation } = useSettingsEntityList();

const items = ref([]);
const loading = ref(false);
const error = ref('');
const mutating = ref(false);
const searchQuery = ref('');
const editModalVisible = ref(false);
const selectedItem = ref(null);

const filteredItems = computed(() => {
  const query = searchQuery.value.trim().toLowerCase();
  const base = query
    ? items.value.filter((item) => {
        const haystack = [
          item.name,
          item.reporting_branch,
          String(item.id),
          isOrgReferenceActive(item) ? 'active' : 'inactive',
        ]
          .join(' ')
          .toLowerCase();
        return haystack.includes(query);
      })
    : items.value;
  return sortOrgReferenceRows(base);
});

const countLabel = computed(() =>
  buildOrgReferenceCountLabel(items.value, 'departments', showInactive.value)
);

const emptyMessage = computed(() => {
  if (searchQuery.value) return 'No departments match your search.';
  if (showInactive.value) return 'No departments found. Click Add to create one.';
  return 'No active departments yet. Click Add to create one.';
});

const rowClass = (data) => orgReferenceRowClass(data);

const notifySuccess = (detail) => {
  toast.add({ severity: 'success', summary: 'Success', detail, life: 2500 });
};

const notifyError = (err, fallback) => {
  toast.add({
    severity: 'error',
    summary: 'Error',
    detail: err?.message || fallback,
    life: 3500,
  });
};

const afterMutationReload = () => afterMutation(loadItems);

const loadItems = async () => {
  loading.value = true;
  error.value = '';
  try {
    const list = await fetchDepartmentsList({
      active: showInactive.value ? 'all' : '1',
    });
    items.value = sortOrgReferenceRows(Array.isArray(list) ? list : []);
  } catch (err) {
    error.value = err?.message || 'Failed to load departments.';
    items.value = [];
  } finally {
    loading.value = false;
  }
};

const openCreate = () => {
  selectedItem.value = null;
  editModalVisible.value = true;
};

const openEdit = (item) => {
  selectedItem.value = item;
  editModalVisible.value = true;
};

const handleSave = async (payload) => {
  mutating.value = true;
  try {
    if (payload.id) {
      await updateDepartment({
        department_id: payload.id,
        name: payload.name?.trim(),
        reporting_branch: payload.reporting_branch,
        head_user_id: payload.head_user_id ?? null,
        tag_color: payload.tag_color,
        tag_text_color: payload.tag_text_color,
        is_active: payload.is_active ? 1 : 0,
      });
      notifySuccess('Department updated');
    } else {
      await createDepartment({
        name: payload.name?.trim(),
        reporting_branch: payload.reporting_branch,
        tag_color: payload.tag_color,
        tag_text_color: payload.tag_text_color,
        is_active: payload.is_active ? 1 : 0,
      });
      notifySuccess('Department created');
    }
    editModalVisible.value = false;
    await afterMutationReload();
  } catch (err) {
    notifyError(err, 'Failed to save department.');
  } finally {
    mutating.value = false;
  }
};

const handleDelete = async ({ id }) => {
  mutating.value = true;
  try {
    await deleteDepartment({ department_id: id });
    notifySuccess('Department deleted');
    editModalVisible.value = false;
    await afterMutationReload();
  } catch (err) {
    notifyError(err, 'Failed to delete department.');
  } finally {
    mutating.value = false;
  }
};

const confirmDeactivate = (item) => {
  confirm.require({
    header: 'Deactivate department',
    message: `"${item.name}" will be hidden from profile options. Linked employees and positions keep their records.`,
    icon: 'pi pi-exclamation-triangle',
    rejectLabel: 'Cancel',
    acceptLabel: 'Deactivate',
    acceptClass: 'p-button-warning',
    accept: () => deactivateItem(item),
  });
};

const deactivateItem = async (item) => {
  mutating.value = true;
  try {
    await updateDepartmentStatus({
      department_id: item.id,
      is_active: false,
    });
    notifySuccess('Department deactivated');
    await afterMutationReload();
  } catch (err) {
    notifyError(err, 'Failed to deactivate department.');
  } finally {
    mutating.value = false;
  }
};

const confirmDelete = (item) => {
  confirm.require({
    header: 'Delete department',
    message: `Delete "${item.name}"? This cannot be undone.`,
    icon: 'pi pi-exclamation-triangle',
    rejectLabel: 'Cancel',
    acceptLabel: 'Delete',
    acceptClass: 'p-button-danger',
    accept: () => handleDelete({ id: item.id }),
  });
};

onMounted(() => {
  loadItems();
});

watch(showInactive, () => {
  loadItems();
});
</script>

<style scoped>
.settings-org-section {
  display: flex;
  flex-direction: column;
  min-height: 0;
  flex: 1;
}

.settings-org-section__body {
  flex: 1;
  min-height: 0;
  overflow: auto;
  padding: 1rem 1.5rem 1.5rem;
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.settings-org-section__state {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 12rem;
}

.settings-org-section__message {
  margin: 0;
}

.settings-org-section__empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  min-height: 10rem;
  border: 1px dashed var(--crc-panel-border, var(--surface-border));
  border-radius: 0.75rem;
  color: var(--crc-text-muted, var(--text-color-secondary));
  text-align: center;
}

.settings-org-section__empty-icon {
  font-size: 1.5rem;
}

.settings-org-section__empty p {
  margin: 0;
  font-size: 0.875rem;
}

:deep(.settings-org-section__row--inactive) {
  opacity: 0.65;
}
</style>
